import React, { useState, useRef } from 'react'
import { 
  Upload, 
  Download, 
  FileText, 
  Image, 
  Settings, 
  X, 
  CheckCircle, 
  AlertTriangle,
  RefreshCw,
  Database,
  Archive
} from 'lucide-react'
import importExportService from '../services/importExportService'
import toast from 'react-hot-toast'

const ImportExportModal = ({ isOpen, onClose, onImport }) => {
  const [activeTab, setActiveTab] = useState('export') // 'export' or 'import'
  const [exportFormat, setExportFormat] = useState('json')
  const [exportType, setExportType] = useState('all') // 'all', 'stories', 'media'
  const [selectedStories, setSelectedStories] = useState([])
  const [selectedMedia, setSelectedMedia] = useState([])
  const [importOptions, setImportOptions] = useState({
    overwrite: false,
    createBackup: true
  })
  const [importResult, setImportResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef(null)

  // 获取支持的格式
  const supportedFormats = importExportService.getSupportedFormats()

  // 处理导出
  const handleExport = async () => {
    setLoading(true)
    try {
      let result
      
      switch (exportType) {
        case 'stories':
          result = await importExportService.exportStories(selectedStories, exportFormat)
          break
        case 'media':
          result = await importExportService.exportMedia(selectedMedia, exportFormat)
          break
        case 'all':
        default:
          result = await importExportService.exportAllData(exportFormat)
          break
      }
      
      if (result.success) {
        toast.success(`导出成功: ${result.filename}`)
      } else {
        toast.error('导出失败')
      }
    } catch (error) {
      console.error('导出失败:', error)
      toast.error(`导出失败: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // 处理导入
  const handleImport = async (file) => {
    if (!file) return

    setLoading(true)
    setImportResult(null)
    
    try {
      const result = await importExportService.importData(file, importOptions)
      setImportResult(result)
      
      if (result.success) {
        toast.success(`导入成功: ${result.imported} 个项目`)
        if (onImport) {
          onImport(result)
        }
      } else {
        toast.error(`导入失败: ${result.errors.join(', ')}`)
      }
    } catch (error) {
      console.error('导入失败:', error)
      toast.error(`导入失败: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // 处理文件选择
  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      handleImport(file)
    }
  }

  // 获取格式图标
  const getFormatIcon = (format) => {
    switch (format) {
      case 'json': return <FileText className="w-5 h-5" />
      case 'csv': return <Database className="w-5 h-5" />
      case 'xml': return <FileText className="w-5 h-5" />
      case 'zip': return <Archive className="w-5 h-5" />
      default: return <FileText className="w-5 h-5" />
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark rounded-2xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            {activeTab === 'export' ? (
              <Download className="w-6 h-6 text-primary" />
            ) : (
              <Upload className="w-6 h-6 text-primary" />
            )}
            <h2 className="text-2xl font-bold text-white">
              {activeTab === 'export' ? '导出数据' : '导入数据'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 标签页 */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 py-4 px-6 text-center transition-colors ${
              activeTab === 'export'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Download className="w-5 h-5 mx-auto mb-2" />
            导出数据
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`flex-1 py-4 px-6 text-center transition-colors ${
              activeTab === 'import'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Upload className="w-5 h-5 mx-auto mb-2" />
            导入数据
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'export' ? (
            <div className="space-y-6">
              {/* 导出类型选择 */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">选择导出内容</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="flex items-center p-4 border border-white/10 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="exportType"
                      value="all"
                      checked={exportType === 'all'}
                      onChange={(e) => setExportType(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-white">完整数据</div>
                      <div className="text-sm text-gray-400">包含所有故事、媒资和设置</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border border-white/10 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="exportType"
                      value="stories"
                      checked={exportType === 'stories'}
                      onChange={(e) => setExportType(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-white">仅故事</div>
                      <div className="text-sm text-gray-400">只导出故事数据</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border border-white/10 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="exportType"
                      value="media"
                      checked={exportType === 'media'}
                      onChange={(e) => setExportType(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-white">仅媒资</div>
                      <div className="text-sm text-gray-400">只导出媒资文件</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* 导出格式选择 */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">选择导出格式</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(supportedFormats).map(([key, format]) => (
                    <label
                      key={key}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        exportFormat === key
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-primary/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="exportFormat"
                        value={key}
                        checked={exportFormat === key}
                        onChange={(e) => setExportFormat(e.target.value)}
                        className="mr-3"
                      />
                      <div className="flex items-center gap-2">
                        {getFormatIcon(key)}
                        <div>
                          <div className="font-medium text-white">{format.name}</div>
                          <div className="text-xs text-gray-400">{format.extension}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* 导出按钮 */}
              <div className="text-center">
                <button
                  onClick={handleExport}
                  disabled={loading}
                  className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      导出中...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      开始导出
                    </div>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 导入选项 */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">导入选项</h3>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={importOptions.overwrite}
                      onChange={(e) => setImportOptions({ ...importOptions, overwrite: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-white">覆盖已存在的数据</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={importOptions.createBackup}
                      onChange={(e) => setImportOptions({ ...importOptions, createBackup: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-white">导入前创建备份</span>
                  </label>
                </div>
              </div>

              {/* 文件上传区域 */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">选择文件</h3>
                <div
                  className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">点击选择文件或拖拽文件到此处</p>
                  <p className="text-sm text-gray-500">支持 JSON, CSV, XML 格式</p>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.csv,.xml"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* 导入结果 */}
              {importResult && (
                <div className="bg-dark-light/50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-3">导入结果</h4>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">{importResult.imported}</div>
                      <div className="text-sm text-gray-400">成功导入</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-400">{importResult.skipped}</div>
                      <div className="text-sm text-gray-400">跳过</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-400">{importResult.errors.length}</div>
                      <div className="text-sm text-gray-400">错误</div>
                    </div>
                  </div>

                  {importResult.details.length > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-semibold text-white mb-2">详细信息:</h5>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {importResult.details.map((detail, index) => (
                          <div key={index} className="text-sm text-gray-300">
                            • {detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {importResult.errors.length > 0 && (
                    <div>
                      <h5 className="text-sm font-semibold text-red-400 mb-2">错误信息:</h5>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {importResult.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-300">
                            • {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 导入按钮 */}
              <div className="text-center">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      导入中...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      选择文件导入
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImportExportModal
